import { useRef, useEffect } from 'react';
import type { ReactNode } from 'react';
import { Table } from 'react-bootstrap';
import { CenteredLoader } from '../feedback/LoadingSpinner';
import EmptyState from '../feedback/EmptyState';

// ─── Column definition ────────────────────────────────────────────────────

export interface AppColumn<T = Record<string, unknown>> {
  /** Unique column key — also used as sort key if sortable=true */
  key: string;
  /** Column header label */
  label: ReactNode;
  /** Enable click-to-sort on this column */
  sortable?: boolean;
  /** Fixed column width, e.g. '120px' */
  width?: string;
  /** Text alignment inside cells */
  align?: 'left' | 'center' | 'right';
  /** Custom cell renderer. Falls back to row[key] when omitted. */
  render?: (row: T, index: number) => ReactNode;
}

// ─── Table props ──────────────────────────────────────────────────────────

export interface AppTableProps<T extends object = Record<string, unknown>> {
  columns: AppColumn<T>[];
  data: T[];
  /** Used for React key prop. Provide a field name or callback. */
  rowKey: keyof T | ((row: T, index: number) => string | number);
  loading?: boolean;
  /** Currently active sort column key */
  sortKey?: string;
  /** Current sort direction */
  sortOrder?: 'asc' | 'desc';
  /** Called when a sortable header is clicked */
  onSort?: (key: string) => void;
  /** Called on row click */
  onRowClick?: (row: T) => void;
  /** Custom class applied to the <tbody><tr> */
  rowClassName?: (row: T) => string;
  emptyTitle?: string;
  emptyMessage?: string;
  emptyIcon?: ReactNode;
  emptyAction?: ReactNode;
  /** Content rendered below the table (e.g. pagination) */
  footer?: ReactNode;
  className?: string;
  /** Passed straight to react-bootstrap Table */
  hover?: boolean;
  striped?: boolean;
  size?: 'sm';
  /** Enable row checkboxes for multi-select */
  selectable?: boolean;
  /** Controlled set of selected row keys */
  selectedKeys?: Set<string | number>;
  /** Called when selection changes */
  onSelectionChange?: (keys: Set<string | number>) => void;
}

// ─── Sort icon ────────────────────────────────────────────────────────────

const SortIcon = ({ active, order }: { active: boolean; order?: 'asc' | 'desc' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={active ? 'table-sort-icon active' : 'table-sort-icon'}
    aria-hidden="true"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    {active && order === 'desc'
      ? <path d="M6 15l6 6l6 -6" />
      : active && order === 'asc'
        ? <path d="M6 9l6 -6l6 6" />
        : <><path d="M6 9l6 -6l6 6" /><path d="M6 15l6 6l6 -6" /></>
    }
  </svg>
);

// ─── Indeterminate checkbox (select-all) ─────────────────────────────────

const IndeterminateCheckbox = ({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="form-check-input"
      checked={checked}
      onChange={onChange}
    />
  );
};

// ─── Component ────────────────────────────────────────────────────────────

function AppTable<T extends object = Record<string, unknown>>({
  columns,
  data,
  rowKey,
  loading = false,
  sortKey,
  sortOrder,
  onSort,
  onRowClick,
  rowClassName,
  emptyTitle = 'No data',
  emptyMessage,
  emptyIcon,
  emptyAction,
  footer,
  className = '',
  hover = true,
  striped = false,
  size,
  selectable = false,
  selectedKeys,
  onSelectionChange,
}: AppTableProps<T>) {
  const getKey = (row: T, index: number): string | number =>
    typeof rowKey === 'function' ? rowKey(row, index) : String(row[rowKey]);

  // Track last individually-clicked index for shift+click range selection
  const lastClickedIndexRef = useRef<number | null>(null);

  const alignClass = (align?: 'left' | 'center' | 'right') =>
    align === 'center' ? 'text-center' : align === 'right' ? 'text-end' : '';

  const allSelected =
    selectable && data.length > 0 && data.every((row, i) => selectedKeys?.has(getKey(row, i)));
  const someSelected =
    selectable && !allSelected && data.some((row, i) => selectedKeys?.has(getKey(row, i)));

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(data.map((row, i) => getKey(row, i))));
    }
  };

  const colCount = columns.length + (selectable ? 1 : 0);

  return (
    <div className="app-table-wrapper">
      <Table
        hover={hover}
        striped={striped}
        size={size}
        responsive
        className={`app-table ${className}`.trim()}
      >
        <thead>
          <tr>
            {selectable && (
              <th style={{ width: '1px' }} className="ps-3">
                <IndeterminateCheckbox
                  checked={!!allSelected}
                  indeterminate={!!someSelected}
                  onChange={handleSelectAll}
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                style={{ width: col.width }}
                className={alignClass(col.align)}
              >
                {col.sortable && onSort ? (
                  <button
                    type="button"
                    className="app-sort-btn"
                    onClick={() => onSort(col.key)}
                  >
                    <span>{col.label}</span>
                    <SortIcon active={sortKey === col.key} order={sortKey === col.key ? sortOrder : undefined} />
                  </button>
                ) : col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="table-tbody">
          {loading ? (
            <tr>
              <td colSpan={colCount} className="p-0 border-0">
                <CenteredLoader className="my-4" />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="p-0 border-0">
                <EmptyState
                  title={emptyTitle}
                  message={emptyMessage}
                  icon={emptyIcon}
                  action={emptyAction}
                />
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr
                key={getKey(row, index)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={[
                  onRowClick ? 'cursor-pointer' : '',
                  rowClassName ? rowClassName(row) : '',
                  selectable && selectedKeys?.has(getKey(row, index)) ? 'table-active' : '',
                ].filter(Boolean).join(' ')}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {selectable && (
                  <td className="ps-3" style={{ width: '1px' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedKeys?.has(getKey(row, index)) ?? false}
                      onChange={(e) => {
                        if (!onSelectionChange || !selectedKeys) return;
                        const key = getKey(row, index);
                        const next = new Set(selectedKeys);
                        const shiftHeld = (e.nativeEvent as MouseEvent).shiftKey;
                        if (shiftHeld && lastClickedIndexRef.current !== null) {
                          // Range select/deselect from last clicked to current
                          const start = Math.min(lastClickedIndexRef.current, index);
                          const end = Math.max(lastClickedIndexRef.current, index);
                          const willCheck = !selectedKeys.has(key);
                          for (let i = start; i <= end; i++) {
                            const k = getKey(data[i], i);
                            if (willCheck) next.add(k);
                            else next.delete(k);
                          }
                        } else {
                          if (next.has(key)) next.delete(key);
                          else next.add(key);
                        }
                        lastClickedIndexRef.current = index;
                        onSelectionChange(next);
                      }}
                    />
                  </td>
                )}
                {columns.map((col) => (
                  <td key={col.key} className={alignClass(col.align)}>
                    {col.render
                      ? col.render(row, index)
                      : (row as Record<string, unknown>)[col.key] as ReactNode}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {footer && (
        <div className="app-table-footer">{footer}</div>
      )}
    </div>
  );
}

export default AppTable;
